import { useEffect, useRef, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import SvgCheck from '@jotta/ui/SvgCheck'
import SvgEdit from '@jotta/ui/SvgEdit'

export interface AlbumTitleProps {
  title: string
  canEdit: boolean
  onSave: (newTitle: string) => Promise<void>
  isPending: boolean
}

export function AlbumTitle({
  title,
  canEdit,
  onSave,
  isPending,
}: AlbumTitleProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [originalTitle, setOriginalTitle] = useState<string>('')
  const [draftTitle, setDraftTitle] = useState(title)

  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isEditing) {
      setDraftTitle(title)
    }
  }, [title, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const startEdit = () => {
    setOriginalTitle(title)
    setDraftTitle(title)
    setIsEditing(true)
  }

  const closeEditor = () => {
    setIsEditing(false)
    requestAnimationFrame(() => headingRef.current?.focus())
  }

  const save = async () => {
    if (isPending) {
      return
    }

    const nextTitle = draftTitle.trim()
    const currentTitle = title.trim()

    if (!nextTitle) {
      setDraftTitle(title)
      closeEditor()
      return
    }

    if (nextTitle === currentTitle) {
      closeEditor()
      return
    }

    try {
      await onSave(nextTitle)
      closeEditor()
    } catch {
      // Keep the editor open on failure. Caller handles error messaging.
    }
  }

  const cancel = () => {
    setDraftTitle(title)
    closeEditor()
  }

  if (!canEdit) {
    return <h1 className="text-xl font-bold text-primary">{title}</h1>
  }

  return (
    <div className="w-full min-w-0">
      {!isEditing ? (
        <div className="min-w-0">
          <span className="inline-flex min-w-0 items-baseline gap-1">
            <h1
              ref={headingRef}
              id="album-title-heading"
              tabIndex={-1}
              className="m-0 min-w-0 break-words text-xl font-bold text-primary outline-none focus:outline-none focus-visible:outline-none"
            >
              {title || t`No Title`}
            </h1>

            <button
              type="button"
              aria-label={t`Edit album title`}
              data-testid="ACTION_CHANGE_TITLE"
              className="relative top-[1px] inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-transparent active:bg-btn-tonal-hover hover:bg-btn-tonal-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-m3-primary"
              onClick={startEdit}
            >
              <SvgEdit
                aria-hidden="true"
                focusable="false"
                className="h-4 w-4 text-on-surface"
              />
            </button>
          </span>
        </div>
      ) : (
        <form
          className="w-full min-w-0"
          onSubmit={e => {
            e.preventDefault()
            void save()
          }}
        >
          <div className="flex w-full min-w-0 items-center gap-2">
            <div className="flex h-10 w-full min-w-0 flex-1 items-center rounded-lg bg-surface-container-low px-3 py-0 xl:w-[300px] xl:flex-none">
              <label className="sr-only" htmlFor="album-title-input">
                <Trans>Album title</Trans>
              </label>

              <input
                id="album-title-input"
                ref={inputRef}
                type="text"
                name="title"
                value={draftTitle}
                placeholder={originalTitle || t`No Title`}
                aria-describedby="album-title-help"
                className="w-full min-w-0 border-none bg-transparent text-xl font-bold text-primary outline-none"
                onChange={e => {
                  setDraftTitle(e.currentTarget.value)
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    cancel()
                  }
                }}
              />
            </div>

            <button
              type="submit"
              className="btn btn-tonal h-10 w-10 shrink-0 p-0"
              aria-label={t`Save album title`}
              disabled={isPending}
            >
              <SvgCheck className="h-4 w-4 text-on-surface" />
            </button>
          </div>

          <span id="album-title-help" className="sr-only">
            <Trans>Press Enter to save, or Escape to cancel.</Trans>
          </span>
        </form>
      )}
    </div>
  )
}
